import { assessmentNavigation } from './assessmentNavigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeDashboardLayout from '../HomeDashboardLayout';
import HeaderProfileSection from '../HeaderProfileSection';
import MyWalletSidebar from '../MyWalletSidebar';
import { fetchCoinOrderAmounts, updateManualPaymentOrder } from '../../services/coinPurchaseService';
import { SUBSCRIPTION_ROUTES } from './subscriptionRoutes';
import { GAMIFICATION_ROUTES } from '../gamification/gamificationRoutes';
import { readExamCheckout } from './examDraft';
import '../../dashboard/Dashboard.css';
import '../HomeDashboard.css';
import '../gamification/Coinstore.css';
import '../../questionBank/QuestionBankList.css';
import './SelectPaymentMethod.css';
import './ManualTransfer.css';
import '../DashboardSkeleton.css';

const money = (value) => `₹ ${Number(value).toFixed(2)}`;

export default function ManualTransfer() {
  const location = useLocation();
  const search = new URLSearchParams(location.search || '');
  const savedExam = readExamCheckout();
  const examState = location.state?.examPurchase ? location.state : search.get('kind') === 'exam' && (!search.get('vk_order_id') || search.get('vk_order_id') === savedExam?.examSubscriptionOrder?.vk_order_id) ? savedExam : null;
  const isExam = Boolean(examState?.examPurchase);
  const navigate = useNavigate();
  const isRapid = Boolean(location.state?.rapidFirePlan);
  const isAssessment = Boolean(location.state?.reattemptPack);
  const isAssessmentSubscription = Boolean(location.state?.assessmentPurchase);
  const isSubject = Boolean(location.state?.subjectOrder);
  const plan = examState?.examPurchase || location.state?.rapidFirePlan || location.state?.reattemptPack || location.state?.assessmentPurchase || location.state?.coinPack || (isSubject ? { name: location.state?.subjectNames } : null);
  const order = examState?.examSubscriptionOrder || location.state?.rapidFireOrder || location.state?.assessmentOrder || location.state?.assessmentSubscriptionOrder || location.state?.coinOrder || location.state?.subjectOrder;
  const gateway = examState?.paymentGateway || location.state?.paymentGateway;
  const orderId = order?.vk_order_id;
  const planName = isExam ? plan?.name : isRapid ? `${plan?.attempt_amount || 0} Rapid Attempts` : isAssessment ? `${plan?.attempt_amount || plan?.amount || 0} Assessment Attempts` : isAssessmentSubscription ? plan?.name : isSubject ? (location.state?.subjectNames || 'Subject Subscription') : `${plan?.coins || 0} Coins`;
  const returnPath = isExam ? SUBSCRIPTION_ROUTES.examPlans : isRapid ? SUBSCRIPTION_ROUTES.rapidFireAttempts
    : isAssessment ? SUBSCRIPTION_ROUTES.assessmentReattempts
    : isAssessmentSubscription ? SUBSCRIPTION_ROUTES.assessmentPlans
    : isSubject ? SUBSCRIPTION_ROUTES.subjectPlans
    : location.state?.coinStorePath === GAMIFICATION_ROUTES.coinStore
      ? GAMIFICATION_ROUTES.coinStore : SUBSCRIPTION_ROUTES.coinStore;
  const [amounts, setAmounts] = useState(null);
  const [loading, setLoading] = useState(Boolean(orderId && gateway));
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const requestRef = useRef(null);

  const loadDetails = useCallback(() => {
    if (!orderId || !gateway?.payment_gateway_id) return Promise.resolve(null);
    if (isExam && !['bank-transfer', 'upi-transfer'].includes(gateway.slug)) return Promise.reject(new Error('This transfer method is unavailable.'));
    if (!requestRef.current) {
      requestRef.current = updateManualPaymentOrder(orderId, gateway.payment_gateway_id)
        .then(() => fetchCoinOrderAmounts(orderId))
        .then((data) => {
          if (data.final_amount == null || !Number.isFinite(Number(data.final_amount)) || Number(data.final_amount) <= 0) {
            throw new Error('The server did not return a payable amount.');
          }
          return data;
        });
    }
    return requestRef.current;
  }, [orderId, gateway?.payment_gateway_id, gateway?.slug, isExam]);

  useEffect(() => {
    let active = true;
    loadDetails().then((data) => {
      if (active) { setAmounts(data); setLoading(false); }
    }).catch((requestError) => {
      if (active) { setError(requestError.message || 'Unable to load transfer details.'); setLoading(false); }
    });
    return () => { active = false; };
  }, [loadDetails]);

  const retry = async () => {
    requestRef.current = null;
    setError('');
    setLoading(true);
    try { setAmounts(await loadDetails()); }
    catch (requestError) { setError(requestError.message || 'Unable to load transfer details.'); }
    finally { setLoading(false); }
  };

  const coupon = amounts?.discounts?.coupon;
  const coinDiscount = amounts?.discounts?.coins;
  const qrUrl = gateway?.slug === 'upi-transfer' || isExam ? gateway?.gateway_img_url : null;
  const title = gateway?.slug === 'upi-transfer' ? 'UPI Transfer' : 'Bank Details';
  const safeDescription = DOMPurify.sanitize(String(gateway?.description || ''), {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li', 'div', 'span', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'h3', 'h4', 'a'],
    ALLOWED_ATTR: ['href'],
  });
  const shareDetail = async (copy = false) => {
    const container = document.createElement('div');
    container.innerHTML = safeDescription;
    const text = `${planName}\nOrder ID: ${orderId}\nAmount: ${money(amounts.final_amount)}\n${gateway.share_description || container.textContent || ''}${qrUrl ? `\n${qrUrl}` : ''}`;
    try {
      if (!copy && navigator.share) await navigator.share({ title, text });
      else { await navigator.clipboard.writeText(text); setActionMessage('Payment details copied.'); }
    } catch (failure) { if (failure.name !== 'AbortError') setActionMessage('Unable to copy or share payment details.'); }
  };

  return (
    <>
      <style>{`.homeDashboard-wrapper .page-content { padding-right: 0; }
        .homeDashboard-wrapper .main-content { height: 100vh; overflow: auto; box-sizing: border-box; padding-bottom: 35px; min-height: 0; }`}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page page-padding start-gamification coin-payment-page manual-transfer-page">
          <div className="start-quiz qbl-main instruction-main">
            <div className="hideProfile-page"><div className="categories-page-header mb-0">
              <div><button type="button" className="categories-back-button" onClick={() => isAssessmentSubscription ? navigate(SUBSCRIPTION_ROUTES.paymentMethod, { state: { assessmentPurchase: plan, assessmentSubscriptionOrder: order, paymentGateways: gateway ? [gateway] : [] } }) : navigate(returnPath)} aria-label="Go back"><i className="bi bi-chevron-left" /></button></div>
              <div><h4 className="d-flex categories-page-title"> </h4></div>
              <div><HeaderProfileSection /></div>
            </div></div>
            <div className="coine-balance-wrapper">
              <div className="rapid-fire-header"><h1 className="qb-title">{title}</h1></div>
              {!plan || !orderId || !gateway ? <p role="alert">Select a purchase plan and payment method to continue.</p> : (
                <div className="coin-payment-columns manual-transfer__columns">
                  <section className="manual-transfer__details">
                    {qrUrl && !imageError && (!isExam || (amounts && !error)) ? <img className="manual-transfer__qr" src={qrUrl} alt={gateway.slug === "upi-transfer" ? "UPI payment QR code" : "Bank transfer details"} onError={() => setImageError(true)} /> : null}
                    {qrUrl && imageError ? <p role="alert">Unable to load the payment QR code.</p> : null}
                    {safeDescription && (!isExam || (amounts && !error)) ? <div className="manual-transfer__instructions" dangerouslySetInnerHTML={{ __html: safeDescription }} /> : null}
                  </section>
                  <section className="manual-transfer__summary">
                    <h2>{planName}</h2>
                    <p>Product Type: {isExam ? (plan?.productType || 'Exam Subscription') : isRapid ? 'Rapid Attempts' : isAssessment ? 'Assessment Attempts' : isAssessmentSubscription ? 'Exam Assessment Subscription' : isSubject ? 'Subject Subscription' : 'Coins'}</p>
                    <p>Order ID: <strong>{amounts?.vk_order_id || orderId}</strong></p>
                    {loading ? <span className="dashboard-skeleton dashboard-skeleton--line" role="status" aria-label="Loading order amount" /> : null}
                    {error ? <p role="alert">{error} <button type="button" onClick={retry}>Retry</button></p> : null}
                    {amounts?.final_amount != null && !error ? <p className="manual-transfer__amount">Final Payable: {money(amounts.final_amount)}</p> : null}
                    {coupon ? <p>Coupon {coupon.code}: - {money(coupon.discount_amount)} ({coupon.discount_value}{coupon.discount_type === 'percentage' ? '%' : ''})</p> : null}
                    {coinDiscount ? <p>Coins used: {coinDiscount.coins_used}, discount: - {money(coinDiscount.discount_amount)}</p> : null}
                    {amounts?.discounts?.total_discount != null ? <p>Total Discount: - {money(amounts.discounts.total_discount)}</p> : null}
                    {isExam && amounts && !error ? <div><button type="button" onClick={() => shareDetail(true)}>Copy payment details</button><button type="button" onClick={() => shareDetail()}>Share payment details</button><button type="button" onClick={() => navigate(`${SUBSCRIPTION_ROUTES.paymentResult}?kind=exam`, { state: { vkOrderId: orderId, purchaseType: 'examSubscription', stateId: plan?.stateId, storePath: SUBSCRIPTION_ROUTES.examPlans } })}>Check payment status</button>{actionMessage && <p role="status">{actionMessage}</p>}<p>Your subscription will activate after the server confirms payment.</p></div> : null}
                    {isSubject && amounts && !error ? <div><button type="button" onClick={() => navigator.clipboard.writeText(`Subject Subscription: ${planName}\nOrder ID: ${orderId}\n${gateway.share_description || ''}`)}>Copy payment details</button><button type="button" onClick={() => navigate(SUBSCRIPTION_ROUTES.paymentResult, { state: { vkOrderId: orderId, purchaseType: 'subject', stateId: location.state?.subjectStateId, storePath: SUBSCRIPTION_ROUTES.subjectPlans } })}>Check payment status</button></div> : null}
                    {isAssessmentSubscription && amounts && !error ? <div><button type="button" onClick={() => navigator.clipboard.writeText(`Assessment Subscription: ${planName}\nOrder ID: ${orderId}\n${gateway.share_description || ''}`)}>Copy payment details</button><button type="button" onClick={() => navigate(SUBSCRIPTION_ROUTES.paymentResult, { state: { ...assessmentNavigation(plan), vkOrderId: orderId, purchaseType: 'assessmentSubscription', stateId: plan?.stateId, storePath: SUBSCRIPTION_ROUTES.assessmentPlans } })}>Check payment status</button></div> : null}
                  </section>
                </div>
              )}
            </div>
          </div>
          <MyWalletSidebar withTopOffset={false} />
        </div>
      </HomeDashboardLayout>
    </>
  );
}
